import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, PaperAirplaneIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import { getApiUrl, API_CONFIG } from '../config/api';
import { useTranslation } from 'react-i18next';

// Sample queries for "Try asking about" suggestions
const SAMPLE_QUERIES = [
  'Review my rental lease agreement',
  'What are my rights as a tenant?',
  'Help me create a will',
  'I need to write a demand letter',
  'How do I file a small claims case?',
];

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('common:greeting.morning');
    if (hour < 18) return t('common:greeting.afternoon');
    return t('common:greeting.evening');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim()) {
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      // Call the classify endpoint
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.CLASSIFY_QUERY), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: query.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to classify query');
      }

      const data = await response.json();
      const activity = data.activity;

      // Map activity to route (must match routes in App.tsx)
      const activityRoutes: Record<string, string> = {
        'Review Legal Documents': '/review-contract',
        'Legal Questions': '/legal-questions',
        'Generate a Will': '/generate-will',
        'Generate a Bill of Sale': '/generate-bill-of-sale',
        'Legal Letters': '/legal-letters',
        'Name Change': '/name-change',
        'Small Claims': '/small-claims',
        'Tenant Rights': '/tenant-rights',
        'Employment Documents': '/employment-advisor',
        'Deadline Calculator': '/deadline-calculator',
        'Legal Glossary': '/legal-glossary',
        'Contract Negotiation': '/contract-negotiation',
        'AI Advisor': '/ai-advisor',
        'Business Formation': '/business-formation',
        'Trademark Search': '/trademark-search',
        'Business Contracts': '/business-contracts',
      };

      const route = activityRoutes[activity];

      if (route) {
        // Navigate to the appropriate activity with context
        setRetryCount(0);
        navigate(route, { state: { initialQuery: query.trim() } });
      } else {
        // No appropriate activity found
        if (retryCount === 0) {
          // First attempt - ask user to be more specific
          setErrorMessage('I couldn\'t determine the best activity for your query. Please try being more specific about what you need help with.');
          setRetryCount(1);
        } else {
          // Second attempt - direct to sidebar
          setErrorMessage('I\'m still unable to find a matching activity. Please browse the menu in the sidebar to select the activity that best fits your needs.');
          setRetryCount(0);
        }
      }
    } catch (error) {
      console.error('Error classifying query:', error);
      setErrorMessage('An error occurred while processing your request. Please try again or select an activity from the sidebar.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setErrorMessage(null);
    setRetryCount(0);
  };

  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      {/* Logo */}
      <div className="mb-8">
        <img src="/scales-logo.png" alt="Scales of Justice" className="h-24 w-24" />
      </div>

      {/* Greeting */}
      <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
        {getGreeting()}, {user?.username || 'John'}
      </h1>
      <p className="text-xl text-gray-600 dark:text-gray-400 mb-12">{t('common:dashboard.helpPrompt')}</p>

      {/* Search Bar */}
      <form onSubmit={handleSubmit} className="w-full max-w-3xl">
        <div className="relative flex items-center">
          <div className="absolute left-4">
            <MagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (errorMessage) setErrorMessage(null);
            }}
            placeholder="Describe what you need help with..."
            className="w-full pl-12 pr-12 py-4 text-lg border-2 border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          <div className="absolute right-4 flex items-center space-x-2">
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="p-2 text-blue-500 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperAirplaneIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mt-4 flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <ExclamationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200">{errorMessage}</p>
          </div>
        )}
      </form>

      {/* Try asking about suggestions */}
      <div className="mt-8 text-center w-full max-w-3xl">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Try asking about:</p>
        <div className="flex flex-wrap justify-center gap-2">
          {SAMPLE_QUERIES.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
